import React from "react";
import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  useColorModeValue,
  Select,
} from "@chakra-ui/react";
import { MdArrowDropUp, MdOutlineCalendarToday, MdBarChart } from "react-icons/md";
import Card from "components/card/Card.js";
import LineChart from "components/charts/LineChart";

export default function TaskCompletionTrends(props) {
  const { taskCompletionTrends, timeFilter, onTimeFilterChange, ...rest } = props;

  // Chakra Color Mode
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "whiteAlpha.700");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const iconColor = useColorModeValue("brand.500", "white");
  const bgButton = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const bgHover = useColorModeValue(
    { bg: "secondaryGray.400" },
    { bg: "whiteAlpha.50" }
  );
  const bgFocus = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.100" }
  );

  // Prepare chart data
  const chartData = taskCompletionTrends || [];
  
  // Debug: Log the received data
  console.log('📊 TaskCompletionTrends component received data:', {
    taskCompletionTrends,
    chartDataLength: chartData.length,
    sampleData: chartData.slice(0, 3),
    dataStructure: chartData[0] ? Object.keys(chartData[0]) : 'No data'
  });
  
  // Calculate total tasks for the period
  const totalTasks = chartData.reduce((sum, day) => sum + (day.totalTasks || 0), 0);
  const totalSuccessful = chartData.reduce((sum, day) => sum + (day.successfulTasks || 0), 0);
  const successRate = totalTasks > 0 ? (totalSuccessful / totalTasks) * 100 : 0;

  // Format data for LineChart component
  const lineChartData = [
    {
      name: "Tasks Completed",
      data: chartData.map(day => day.totalTasks || 0),
      color: "#4318FF",
    },
    {
      name: "Successful Tasks", 
      data: chartData.map(day => day.successfulTasks || 0),
      color: "#6AD2FF",
    }
  ];
  
  // Debug: Log the formatted chart data
  console.log('📊 TaskCompletionTrends formatted chart data:', {
    lineChartData,
    totalTasksData: lineChartData[0].data,
    successfulTasksData: lineChartData[1].data,
    categories: chartData.map(day => {
      const date = new Date(day.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    })
  });

  const lineChartOptions = {
    chart: {
      toolbar: {
        show: false,
      },
      dropShadow: {
        enabled: true,
        top: 13,
        left: 0,
        blur: 10,
        opacity: 0.1,
        color: "#4318FF",
      },
    },
    colors: ["#4318FF", "#6AD2FF"],
    markers: {
      size: 0,
      colors: "white",
      strokeColors: "#7551FF",
      strokeWidth: 3,
      strokeOpacity: 0.9,
      strokeDashArray: 0,
      fillOpacity: 1,
      discrete: [],
      shape: "circle",
      radius: 2,
      offsetX: 0,
      offsetY: 0,
      showNullDataPoints: true,
    },
    tooltip: {
      theme: "dark",
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      type: "line",
    },
    xaxis: {
      type: "numeric",
      categories: chartData.map(day => {
        const date = new Date(day.date);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }),
      labels: {
        style: {
          colors: "#A3AED0",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      show: true,
      labels: {
        style: {
          colors: "#A3AED0",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "right",
    },
    grid: {
      show: false,
      column: {
        color: ["#7551FF", "#39B8FF"],
        opacity: 0.5,
      },
    },
    color: ["#7551FF", "#39B8FF"],
  };

  return (
    <Card
      justifyContent='center'
      align='center'
      direction='column'
      w='100%'
      mb='0px'
      {...rest}>
      <Flex justify='space-between' ps='0px' pe='20px' pt='5px'>
        <Flex align='center' w='100%'>
          <Button
            bg={boxBg}
            fontSize='sm'
            fontWeight='500'
            color={textColorSecondary}
            borderRadius='7px'>
            <Icon
              as={MdOutlineCalendarToday}
              color={textColorSecondary}
              me='4px'
            />
            This month
          </Button>
          <Button
            ms='auto'
            align='center'
            justifyContent='center'
            bg={bgButton}
            _hover={bgHover}
            _focus={bgFocus}
            _active={bgFocus}
            w='37px'
            h='37px'
            lineHeight='100%'
            borderRadius='10px'
            {...rest}>
            <Icon as={MdBarChart} color={iconColor} w='24px' h='24px' />
          </Button>
        </Flex>
      </Flex>
      <Flex w='100%' flexDirection={{ base: "column", lg: "row" }}>
        <Flex flexDirection='column' me='20px' mt='28px'>
          <Text
            color={textColor}
            fontSize='34px'
            textAlign='start'
            fontWeight='700'
            lineHeight='100%'>
            {totalTasks.toLocaleString()}
          </Text>
          <Flex align='center' mb='20px'>
            <Text
              color='secondaryGray.600'
              fontSize='sm'
              fontWeight='500'
              mt='4px'
              me='12px'>
              Total Tasks
            </Text>
            <Flex align='center'>
              <Icon as={MdArrowDropUp} color='green.500' me='2px' mt='2px' />
              <Text color='green.500' fontSize='sm' fontWeight='700'>
                {successRate.toFixed(1)}%
              </Text>
            </Flex>
          </Flex>

          <Flex align='center'>
            <Icon as={MdBarChart} color='green.500' me='6px' w='24px' h='24px' />
            <Text color='secondaryGray.600' fontSize='sm' fontWeight='500'>
              Task completion trends
            </Text>
          </Flex>
        </Flex>
        <Box minH='260px' minW='75%' mt='auto'>
          {chartData.length > 0 ? (
            <LineChart
              chartData={lineChartData}
              chartOptions={lineChartOptions}
            />
          ) : (
            <Flex 
              align="center" 
              justify="center" 
              h="260px" 
              direction="column"
              color="gray.500"
            >
              <Text fontSize="lg" mb="2">No task data available</Text>
              <Text fontSize="sm">Task completion trends will appear here once data is available</Text>
            </Flex>
          )}
        </Box>
      </Flex>
    </Card>
  );
}
